import React, {useEffect, useRef, useState} from 'react'
import {Alert, Checkbox, Col, Form, Input, InputNumber, Row, Select, Space, Typography} from 'antd'
import FormErrorMessage from '../base/FormErrorMessage'
import {validateMessages} from '../../app/util'
import NewPiecesFormButton from './NewPiecesFormButton'
import {loadForm} from '../../app/cacheUtil'
import {RequiredTitle} from './common'
import {QuestionCircleOutlined} from "@ant-design/icons";
import {is_admin_user} from "../../app/role";
import BusinessOriginal from "../../asset/image/business-original.jpeg";
import BusinessPrints from "../../asset/image/business-prints.jpeg";
import BusinessCommission from "../../asset/image/business-commission.jpeg";
import BusinessLicense from "../../asset/image/business-license.jpeg";

const {Title, Text} = Typography
const {Option} = Select

export default (props) => {
    const width = props.width
    const height = props.height
    const depth = props.depth
    const [validateError, setValidateError] = useState([])
    const [hasPrintsLayout, setHasPrintsLayout] = useState(false)
    const [hasOriginalLayout, setHasOriginalLayout] = useState(false)
    const [hasCommissionLayout, setHasCommissionLayout] = useState(false)
    const [hasLicenseLayout, setHasLicenseLayout] = useState(false)
    const [printsDesc, setPrintsDesc] = useState(getPrintsCostOption(width, height)[1] ?? 'Prices for prints are not available.')
    const formRef = useRef(null)

    const onFinishFailed = ({values, errorFields, outOfDate}) => {
        setValidateError(errorFields)
        window.scrollTo(0, 0)
        window.scrollTop = 0
    }

    const businessTypes = [
        {
            label: 'Sell Original',
            value: 'original',
            img: <img width={240} height={160} style={{marginLeft: 'auto', marginRight: 'auto'}}
                      src={BusinessOriginal} alt={"original"}/>
        },
        {
            label: 'Sell Print',
            value: 'prints',
            img: <img width={240} height={160} style={{marginLeft: 'auto', marginRight: 'auto'}}
                      src={BusinessPrints} alt={"prints"}/>
        },
        {
            label: 'Sell Commission',
            value: 'commission',
            img: <img width={240} height={160} style={{marginLeft: 'auto', marginRight: 'auto'}}
                      src={BusinessCommission} alt={"commission"}/>
        },
        {
            label: 'License Art',
            value: 'licensing',
            img: <img width={240} height={160} style={{marginLeft: 'auto', marginRight: 'auto'}}
                      src={BusinessLicense} alt={"licensing"}/>
        }
    ]

    const onSelectionChange = (e) => {
        const v = e.target.value
        if (v === 'original') {
            setHasOriginalLayout(!hasOriginalLayout)
        } else if (v === 'prints') {
            setHasPrintsLayout(!hasPrintsLayout)
        } else if (v === 'commission') {
            setHasCommissionLayout(!hasCommissionLayout)
        } else if (v === 'licensing') {
            setHasLicenseLayout(!hasLicenseLayout)
        }
    }

    function getPrintsCostOption(width, height, commissionRate = 0.2) {
        try {
            width = Math.abs(parseFloat(width));
            height = Math.abs(parseFloat(height));
            if (height <= 0 || width <= 0) {
                return ['', ''];
            }

            let large, small;
            if (width > height) {
                large = width;
                small = height;
            } else {
                large = height;
                small = width;
            }

            let ratio = large / small;
            ratio = Math.round(ratio * 100) / 100;

            const RATIO_MAP = {
                1: '12" x 12"',
                1.25: '11" x 14"',
                1.27: '16" x 20"',
                1.29: '11" x 14"',
                1.30: '14" x 18"',
                1.33: '24" x 30"',
                1.40: '8" x 10"',
                1.45: '30" x 40"',
                1.46: '16" x 24"',
                1.50: '24" x 36"',
                1.55: '15" x 21"'
            }

            const PRINT_COST_MAP = {
                1: 64,
                1.25: 72,
                1.27: 86,
                1.29: 72,
                1.30: 84,
                1.33: 190,
                1.40: 38,
                1.45: 316,
                1.46: 102,
                1.50: 228,
                1.55: 85,
            }

            const FRAME_COST_MAP = {
                1: 122,
                1.25: 112,
                1.27: 195,
                1.29: 0,
                1.30: 0,
                1.33: 471,
                1.40: 0,
                1.45: 0,
                1.46: 0,
                1.50: 0,
                1.55: 0,
            }

            if (!(ratio in RATIO_MAP)) {
                return ['', ''];
            }

            let description = RATIO_MAP[ratio];
            let printCost = PRINT_COST_MAP[ratio];
            let frameCost = FRAME_COST_MAP[ratio];

            let retailPrice = Math.round(printCost * (1 + commissionRate));

            if (frameCost > 0) {
                description += ' Print, Black Wood Frame ';
                retailPrice += frameCost;
            } else {
                description += ' Print Only, Unframed ';
            }

            description += '$' + retailPrice;

            return [retailPrice, description];
        } catch (error) {
            return ['', ''];
        }
    }

    useEffect(() => {
        const cache = loadForm(props.formId)
        if (cache) {
            formRef.current?.setFieldsValue(cache)
        }
        if (props.businessType?.used_type) {
            if (props.businessType.used_type.includes('original')) {
                setHasOriginalLayout(true)
            } else {
                setHasOriginalLayout(false)
            }
            if (props.businessType.used_type.includes('prints')) {
                setHasPrintsLayout(true)
            } else {
                setHasPrintsLayout(false)
            }
        }
    }, [props])

    return (
        <>
            <FormErrorMessage error={validateError}/>
            <Form
                colon={false}
                ref={formRef}
                labelAlign='left'
                layout={'vertical'}
                onFinish={props.onFinish}
                validateMessages={validateMessages}
                onFinishFailed={onFinishFailed}
                style={{
                    marginTop: '16px'
                }}
            >
                <Row
                    style={{
                        paddingLeft: 4,
                        paddingRight: 4,
                        maxWidth: 1280,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                        height: '100%',
                        flexDirection: 'row'
                    }}
                >
                    <Col
                        xs={24}
                        sm={24}
                        md={24}
                        style={{
                            marginTop: 32,
                            display: 'flex',
                            flexDirection: 'column'
                        }}
                    >
                        <Form.Item
                            name='used_type'
                            label={<Title level={4}>Collaboration Type</Title>}
                            style={{display: 'block', marginLeft: 'auto', marginRight: 'auto'}}
                            rules={[
                                {
                                    required: !is_admin_user(props)
                                }
                            ]}
                        >
                            <Checkbox.Group style={{display: 'flex', width: '100%', gap: 4, flexWrap: 'wrap'}}>
                                {businessTypes.map((type) => {
                                    return (
                                        <Col style={{width: 'auto', display: 'flex', flexDirection: 'column'}} xs={24}
                                             sm={5}>
                                            {type.img}
                                            <center>
                                                <h5>
                                                    {type.label}&nbsp;<Checkbox label={type.label} value={type.value}
                                                                                onChange={onSelectionChange}/>
                                                </h5>
                                            </center>
                                        </Col>
                                    )
                                })}
                            </Checkbox.Group>
                        </Form.Item>

                        {hasOriginalLayout && (
                            <>
                                <Row style={{
                                    marginTop: 32,
                                    width: '100%',
                                    maxWidth: 860,
                                    marginLeft: 'auto',
                                    marginRight: 'auto'
                                }}>
                                    <Form.Item
                                        label={<Title level={4}>{RequiredTitle('Original')}</Title>}
                                        style={{display: 'block', width: '100%'}}
                                        rules={[
                                            {
                                                required: true,
                                                type: 'number',
                                                message: 'Invalid price value',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        name='price_type_str'
                                    >
                                        <Input prefix={'$'}/>
                                    </Form.Item>
                                </Row>
                            </>
                        )}
                        {hasPrintsLayout && (
                            <>
                                <Row style={{
                                    marginTop: 32,
                                    width: '100%',
                                    maxWidth: 860,
                                    marginLeft: 'auto',
                                    marginRight: 'auto'
                                }}>
                                    <Title level={4}><span style={{color: 'red'}}>*</span> {RequiredTitle('Print')}
                                    </Title>
                                    <Form.Item
                                        name='inventory_prints_quantity'
                                        label='Quantities'
                                        style={{display: 'block', width: '100%', marginTop: 16}}
                                        rules={[
                                            {
                                                required: true,
                                                type: 'integer',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        initialValue={50}
                                    >
                                        <InputNumber style={{width: '100%'}}/>
                                    </Form.Item>
                                </Row>
                                <Row style={{
                                    marginTop: 16,
                                    width: '100%',
                                    maxWidth: 860,
                                    marginLeft: 'auto',
                                    marginRight: 'auto'
                                }}>
                                    <Form.Item
                                        name='prints_markup_percentage'
                                        label={<a target={'_blank'}
                                                  href={'https://revart.co/sam/faq/docs/doc-giclee-print/'}><QuestionCircleOutlined/> {'Markup'}
                                        </a>}
                                        style={{display: 'block', width: '100%'}}
                                        rules={[
                                            {
                                                required: true,
                                                type: 'number',
                                                transform: (value) => {
                                                    return Number(value)
                                                }
                                            }
                                        ]}
                                        initialValue={20}
                                    >
                                        <Input onChange={(e) => {
                                            try {
                                                let ratio = parseFloat(e.target.value) / 100
                                                ratio = Math.min(Math.max(0, ratio), 1.0)
                                                if (0 <= ratio && ratio <= 1)
                                                    setPrintsDesc(getPrintsCostOption(width, height, ratio)[1])
                                            } catch (e) {
                                            }
                                        }} suffix='%' style={{width: '100%'}}/>
                                    </Form.Item>
                                </Row>
                                <Row style={{
                                    marginTop: 16,
                                    width: '100%',
                                    maxWidth: 860,
                                    marginLeft: 'auto',
                                    marginRight: 'auto'
                                }}>
                                    <div><span style={{color: 'red'}}>*</span> {'Dimensions of work (in inches)'}</div>
                                    <Space style={{marginTop: 9}}>
                                        <Form.Item
                                            label='Height'
                                            name='height'
                                            initialValue={height ?? ''}
                                            rules={[
                                                {
                                                    required: false,
                                                    type: 'number',
                                                    transform: (value) => {
                                                        return Number(value)
                                                    }
                                                }
                                            ]}
                                            noStyle
                                        >
                                            <Input disabled={true} prefix={<Text>H</Text>}/>
                                        </Form.Item>
                                        <Form.Item
                                            label='Width'
                                            name='width'
                                            initialValue={width ?? ''}
                                            rules={[
                                                {
                                                    required: false,
                                                    type: 'number',
                                                    transform: (value) => {
                                                        return Number(value)
                                                    }
                                                }
                                            ]}
                                            noStyle
                                        >
                                            <Input disabled={true} prefix={<Text>W</Text>}/>
                                        </Form.Item>
                                        <Form.Item
                                            label='Depth'
                                            name='depth'
                                            initialValue={depth ?? ''}
                                            rules={[
                                                {
                                                    required: false,
                                                    type: 'number',
                                                    transform: (value) => {
                                                        return Number(value)
                                                    }
                                                }
                                            ]}
                                            noStyle
                                        >
                                            <Input disabled={true} prefix={<Text>D</Text>}/>
                                        </Form.Item>
                                    </Space>
                                    <Form.Item
                                        style={{marginTop: 32}}
                                        label={'Price'}
                                        rules={[{
                                            required: true,
                                        }]}
                                    >
                                        <Select placeholder={printsDesc} value={printsDesc}>
                                            <Option value='select'>{printsDesc}</Option>
                                        </Select>
                                    </Form.Item>
                                </Row>
                            </>
                        )}
                        {(hasCommissionLayout || hasLicenseLayout) && !hasOriginalLayout && !hasPrintsLayout && (
                            <div style={{display: 'flex', width: '100%', marginTop: 12}}>
                                <Alert
                                    style={{maxWidth: 860, marginLeft: 'auto', marginRight: 'auto'}}
                                    message="Note:"
                                    description="We do not have a fixed price for art commission and licensing. We will contact you to discuss pricing and other details."
                                    type="info"
                                    showIcon
                                />
                            </div>
                        )}
                    </Col>
                    <NewPiecesFormButton {...props} stepperCount={3}/>
                </Row>
            </Form>
        </>
    )
}
